import React from "react";
import { BaseEdge, EdgeProps, getSmoothStepPath, Position } from "reactflow";

type RoutingConfig = {
  mode?: "auto" | "manual";
  bendOffset?: number;
};

const clamp = (v: number, min: number, max: number) => Math.max(min, Math.min(max, v));
const STUB = 18;

const normalizeHandleId = (handle?: string | null) => {
  if (!handle) return undefined;
  const h = handle.toLowerCase();
  const map: Record<string, string> = {
    "top-in": "top",
    "top-out": "top",
    "bottom-in": "bottom",
    "bottom-out": "bottom",
    "left-in": "left",
    "left-out": "left",
    "right-in": "right",
    "right-out": "right",
  };
  return map[h] || (["top", "bottom", "left", "right"].includes(h) ? h : undefined);
};

const dirForHandle = (handle?: string | null, fallback?: { x: number; y: number }) => {
  const h = normalizeHandleId(handle);
  if (h === "top") return { x: 0, y: -1 };
  if (h === "bottom") return { x: 0, y: 1 };
  if (h === "left") return { x: -1, y: 0 };
  if (h === "right") return { x: 1, y: 0 };
  return fallback || { x: 0, y: 1 };
};

const positionToDir = (pos?: Position | null) => {
  if (pos === Position.Left) return { x: -1, y: 0 };
  if (pos === Position.Right) return { x: 1, y: 0 };
  if (pos === Position.Top) return { x: 0, y: -1 };
  if (pos === Position.Bottom) return { x: 0, y: 1 };
  return undefined;
};

const dirToPosition = (dir: { x: number; y: number }): Position => {
  if (Math.abs(dir.x) > Math.abs(dir.y)) {
    return dir.x >= 0 ? Position.Right : Position.Left;
  }
  return dir.y >= 0 ? Position.Bottom : Position.Top;
};

export const OrthogonalEdge: React.FC<EdgeProps> = (props) => {
  const {
    sourceX,
    sourceY,
    targetX,
    targetY,
    sourcePosition,
    targetPosition,
    markerEnd,
    markerStart,
    style,
    data,
    sourceHandle,
    targetHandle,
  } = props;

  const routing: RoutingConfig = (data as any)?.routing || {};
  const mode = routing.mode || "auto";
  const offset = clamp(routing.bendOffset ?? 0, -800, 800);

  const dx = targetX - sourceX;
  const dy = targetY - sourceY;
  const adx = Math.abs(dx);
  const ady = Math.abs(dy);

  const inferredSource = ady >= adx ? { x: 0, y: dy >= 0 ? 1 : -1 } : { x: dx >= 0 ? 1 : -1, y: 0 };
  const inferredTarget = ady >= adx ? { x: 0, y: dy >= 0 ? -1 : 1 } : { x: dx >= 0 ? -1 : 1, y: 0 };

  // Prefer explicit handles from props or data; only infer if missing
  const sh = normalizeHandleId(sourceHandle || (data as any)?.source_handle);
  const th = normalizeHandleId(targetHandle || (data as any)?.target_handle);

  const sDir = dirForHandle(sh, positionToDir(sourcePosition) || inferredSource);
  const tDir = dirForHandle(th, positionToDir(targetPosition) || inferredTarget);

  const p0 = { x: sourceX, y: sourceY };
  const p5 = { x: targetX, y: targetY };
  const p1 = { x: p0.x + sDir.x * STUB, y: p0.y + sDir.y * STUB };
  const p4 = { x: p5.x - tDir.x * STUB, y: p5.y - tDir.y * STUB };
  const sourcePos = sourcePosition ?? dirToPosition(sDir);
  const targetPos = targetPosition ?? dirToPosition(tDir);

  // Auto-Mode: reuse React Flow smoothstep routing so 90° matches the rounded variant (radius 0 keeps hard corners)
  if (mode === "auto") {
    const [path] = getSmoothStepPath({
      sourceX,
      sourceY,
      sourcePosition: sourcePos,
      targetX,
      targetY,
      targetPosition: targetPos,
      // mirror SmoothStep routing (rounded) but keep 90° look by using a small radius
      borderRadius: 0,
    });
    return <BaseEdge path={path} markerEnd={markerEnd} markerStart={markerStart} style={style} />;
  }

  // Manual, but without an offset: keep the same routing as auto to avoid flicker/flipping on mode changes.
  if (mode === "manual" && offset === 0) {
    const [path] = getSmoothStepPath({
      sourceX,
      sourceY,
      sourcePosition: sourcePos,
      targetX,
      targetY,
      targetPosition: targetPos,
      borderRadius: 0,
    });
    return <BaseEdge path={path} markerEnd={markerEnd} markerStart={markerStart} style={style} />;
  }

  const sourceIsVertical = sDir.y !== 0;
  const targetIsVertical = tDir.y !== 0;
  const sourceIsHorizontal = sDir.x !== 0;
  const targetIsHorizontal = tDir.x !== 0;

  // Choose routing orientation:
  // - Auto + clear vertical dominance => force vertical_mid (mid-height trunk) so new edges are stable without reload.
  // - Otherwise prefer explicit handles, else fall back to geometry.
  let orientation: "vertical" | "horizontal" | "mixed" | "vertical_mid";
  if (mode === "auto" && ady > adx) {
    orientation = "vertical_mid";
  } else if (sh && th) {
    if (sourceIsVertical && targetIsVertical) orientation = "vertical";
    else if (sourceIsHorizontal && targetIsHorizontal) orientation = "horizontal";
    else orientation = "mixed"; // mixed handles -> simple L
  } else {
    orientation = ady >= adx ? "vertical" : "horizontal";
  }

  let path = "";

  if (orientation === "vertical" || orientation === "vertical_mid") {
    const baseY = orientation === "vertical_mid" ? (p0.y + p5.y) / 2 : (p1.y + p4.y) / 2;
    let trunkY = mode === "manual" && orientation !== "vertical_mid" ? baseY + offset : baseY;
    const delta = p4.y - p1.y || (dy >= 0 ? 1 : -1);
    if (Math.abs(trunkY - p1.y) < 12) trunkY = p1.y + Math.sign(delta) * 12;
    if (Math.abs(trunkY - p4.y) < 12) trunkY = p4.y - Math.sign(delta) * 12;
    path = [
      `M ${p0.x},${p0.y}`,
      `L ${p1.x},${p1.y}`,
      `L ${p1.x},${trunkY}`,
      `L ${p4.x},${trunkY}`,
      `L ${p4.x},${p4.y}`,
      `L ${p5.x},${p5.y}`,
    ].join(" ");
  } else if (orientation === "horizontal") {
    const baseX = (p1.x + p4.x) / 2;
    let trunkX = mode === "manual" ? baseX + offset : baseX;
    const delta = p4.x - p1.x || (dx >= 0 ? 1 : -1);
    if (Math.abs(trunkX - p1.x) < 12) trunkX = p1.x + Math.sign(delta) * 12;
    if (Math.abs(trunkX - p4.x) < 12) trunkX = p4.x - Math.sign(delta) * 12;
    path = [
      `M ${p0.x},${p0.y}`,
      `L ${p1.x},${p1.y}`,
      `L ${trunkX},${p1.y}`,
      `L ${trunkX},${p4.y}`,
      `L ${p4.x},${p4.y}`,
      `L ${p5.x},${p5.y}`,
    ].join(" ");
  } else {
    // mixed handles: simple L-shape between stubs (ignore offset)
    const corner =
      sourceIsVertical && targetIsHorizontal
        ? { x: p4.x, y: p1.y }
        : sourceIsHorizontal && targetIsVertical
        ? { x: p1.x, y: p4.y }
        : { x: p4.x, y: p1.y };
    path = [
      `M ${p0.x},${p0.y}`,
      `L ${p1.x},${p1.y}`,
      `L ${corner.x},${corner.y}`,
      `L ${p4.x},${p4.y}`,
      `L ${p5.x},${p5.y}`,
    ].join(" ");
  }

  return <BaseEdge path={path} markerEnd={markerEnd} markerStart={markerStart} style={style} />;
};
